import {
	faCheck,
	faLock,
	faMinus,
	faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AgGridAtom } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import {
	ACCESS_RIGHT_AREAS,
	ACCESS_RIGHT_TYPES,
	AREA_CAPABILITIES,
} from "../../../constant/permissions";
import {
	useGetRolePermissionsQuery,
	useListRolesQuery,
} from "../../../store/services/role";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { isSystemRole } from "../../../utils/isSystemRole";
import { searchData } from "../../../utils/search";
import { getRoleTableColumns } from "./constant";
import "./role.scss";

const RIGHT_ORDER = [
	ACCESS_RIGHT_TYPES.VIEW,
	ACCESS_RIGHT_TYPES.CREATE,
	ACCESS_RIGHT_TYPES.EDIT,
	ACCESS_RIGHT_TYPES.DELETE,
	ACCESS_RIGHT_TYPES.SYNC,
];

const AREA_ORDER = Object.keys(AREA_CAPABILITIES);

function RoleSettingPage() {
	const { t } = useTranslation();

	const { data: roles = [], isLoading: rolesLoading } = useListRolesQuery();

	const [selectedRoleId, setSelectedRoleId] = useState(null);
	const selectedRole = useMemo(
		() => roles.find((role) => role.id === selectedRoleId) || null,
		[roles, selectedRoleId],
	);

	const { data: permissions = {}, isFetching: permissionsLoading } =
		useGetRolePermissionsQuery(selectedRoleId, { skip: !selectedRoleId });

	const roleColumns = useMemo(() => getRoleTableColumns(t), [t]);

	const [filterItems, setFilterItems] = useState(
		getSearchAddFilter({ hideAddButton: true }),
	);
	const [filteredRoleList, setFilteredRoleList] = useState([]);

	useEffect(() => {
		setFilteredRoleList(roles);
	}, [roles]);

	const onFilterAction = (index, key, info) => {
		const localFilterItems = [...filterItems];
		localFilterItems[index].value = info?.value;
		setFilterItems(localFilterItems);

		let filtered = [...roles];
		localFilterItems.forEach((item) => {
			if (item.key === "search" && item.value) {
				filtered = searchData(item.value, filtered, roleColumns);
			}
		});
		setFilteredRoleList(filtered);
	};

	const renderPermissionCell = (areaKey, rightKey) => {
		const applicable = AREA_CAPABILITIES[areaKey]?.includes(rightKey);
		if (!applicable) {
			return (
				<FontAwesomeIcon
					icon={faMinus}
					className="perm-cell perm-cell--na"
					title={t("roles.permissions.legend.not-applicable")}
				/>
			);
		}
		const granted = permissions?.[areaKey]?.[rightKey] === true;
		return (
			<FontAwesomeIcon
				icon={granted ? faCheck : faXmark}
				className={`perm-cell ${
					granted ? "perm-cell--granted" : "perm-cell--denied"
				}`}
				title={
					granted
						? t("roles.permissions.legend.granted")
						: t("roles.permissions.legend.denied")
				}
			/>
		);
	};

	return (
		<div className="page-container">
			<FilterBar
				items={filterItems}
				onFilterAction={onFilterAction}
				area={ACCESS_RIGHT_AREAS.ROLES}
			/>

			<Row className="flex-grow-1 g-0">
				<Col md={5} className="h-100">
					<div className="ag-grid-parent h-100 pr-md-2">
						<AgGridAtom
							columnDefs={roleColumns}
							rowData={filteredRoleList}
							onRowClicked={(params) => setSelectedRoleId(params.data.id)}
							rowClassRules={{
								"ag-row-selected": (params) =>
									params.data.id === selectedRoleId,
							}}
							overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${
								roles.length === 0
									? t("roles.no-data")
									: t("common.no-results-found")
							}</span>`}
							defaultColDef={{ flex: 1, sortable: true, filter: true }}
						/>
						{rolesLoading && (
							<div className="loading-overlay">
								<div className="spinner-border text-primary" role="status">
									<span className="visually-hidden">
										{t("common.loading")}
									</span>
								</div>
							</div>
						)}
					</div>
				</Col>

				<Col md={7}>
					{selectedRole ? (
						<Card className="m-3">
							<Card.Header className="d-flex align-items-center gap-2 flex-wrap">
								<h5 className="mb-0 d-flex align-items-center gap-2 flex-wrap">
									<span>
										{t("roles.permissions.permissions-for")}{" "}
										{t(`roles.types.${selectedRole.name}`, {
											defaultValue: selectedRole.name,
										})}
									</span>
									{isSystemRole(selectedRole) && (
										<Badge bg="secondary">
											<FontAwesomeIcon
												icon={faLock}
												className="me-1"
												fixedWidth
											/>
											{t("roles.system-role")}
										</Badge>
									)}
								</h5>
							</Card.Header>
							<Card.Body>
								<Alert variant="info" className="mb-3">
									{t("roles.permissions.read-only-note")}
								</Alert>

								{permissionsLoading ? (
									<div className="text-center p-4">
										<Spinner animation="border" />
										<p className="mt-2">{t("roles.permissions.loading")}</p>
									</div>
								) : (
									<>
										<div className="table-responsive">
											<Table bordered hover className="permission-table">
												<thead>
													<tr>
														<th>{t("roles.permissions.area")}</th>
														{RIGHT_ORDER.map((rightKey) => (
															<th key={rightKey} className="text-center">
																{t(
																	`roles.permissions.types.${rightKey.toLowerCase()}`,
																)}
															</th>
														))}
													</tr>
												</thead>
												<tbody>
													{AREA_ORDER.map((areaKey) => (
														<tr key={areaKey}>
															<td>
																<strong>
																	{t(`roles.permissions.areas.${areaKey}`)}
																</strong>
															</td>
															{RIGHT_ORDER.map((rightKey) => (
																<td
																	key={`${areaKey}-${rightKey}`}
																	className="text-center"
																>
																	{renderPermissionCell(areaKey, rightKey)}
																</td>
															))}
														</tr>
													))}
												</tbody>
											</Table>
										</div>

										<div className="perm-legend">
											<span className="perm-legend__item">
												<FontAwesomeIcon
													icon={faCheck}
													className="perm-cell perm-cell--granted"
												/>
												{t("roles.permissions.legend.granted")}
											</span>
											<span className="perm-legend__item">
												<FontAwesomeIcon
													icon={faXmark}
													className="perm-cell perm-cell--denied"
												/>
												{t("roles.permissions.legend.denied")}
											</span>
											<span className="perm-legend__item">
												<FontAwesomeIcon
													icon={faMinus}
													className="perm-cell perm-cell--na"
												/>
												{t("roles.permissions.legend.not-applicable")}
											</span>
										</div>
									</>
								)}
							</Card.Body>
						</Card>
					) : (
						<Card className="m-3 text-center">
							<Card.Body>
								<Alert variant="info">
									{t("roles.permissions.select-role")}
								</Alert>
							</Card.Body>
						</Card>
					)}
				</Col>
			</Row>
		</div>
	);
}

export default RoleSettingPage;
